import React, { useState } from "react";
import { Archive, ArrowLeft, Pencil, Plus } from "lucide-react";

import { chatText, getChatLocale } from "../messages";

const UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

export function relativeTime(iso, now = Date.now(), locale = getChatLocale()) {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) {
      return format.format(Math.round(seconds / size), unit);
    }
  }

  return format.format(0, "minute");
}

function preview(conversation) {
  const text = conversation.last_message_preview;
  if (!text) return null;
  return conversation.last_message_role === "user"
    ? chatText("chat.history.you", { text })
    : text;
}

function ConversationRow({ conversation, isActive, onOpen, onRename, onArchive }) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(conversation.title || "");
  const name = conversation.title || chatText("chat.history.untitled");

  async function save(event) {
    event.preventDefault();
    const value = title.trim();
    if (!value) return;
    await onRename(conversation.id, value);
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <li className="rounded-2xl bg-white p-3 shadow-sm">
        <form onSubmit={save} className="flex flex-col gap-2">
          <input
            aria-label={chatText("chat.history.rename_label")}
            value={title}
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            className="rounded-xl border border-[#0A4730]/15 px-3 py-2 text-sm outline-none focus:border-[#0A4730]/40"
            autoFocus
          />
          <div className="flex justify-end gap-2 text-xs font-medium">
            <button type="button" onClick={() => setIsEditing(false)} className="px-3 py-1.5 text-[#0A4730]/70">
              {chatText("chat.history.cancel")}
            </button>
            <button
              type="submit"
              disabled={!title.trim()}
              className="rounded-full bg-[#0A4730] px-3 py-1.5 text-white disabled:opacity-40"
            >
              {chatText("chat.history.save")}
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li
      className={`flex items-start gap-2 rounded-2xl p-3 shadow-sm ${
        isActive ? "bg-[#C0F6B7]/50" : "bg-white"
      }`}
    >
      <button
        type="button"
        onClick={() => onOpen(conversation.id)}
        aria-current={isActive ? "true" : undefined}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold text-[#042416]">{name}</span>
          <time dateTime={conversation.last_message_at} className="shrink-0 text-[11px] text-[#0A4730]/55">
            {relativeTime(conversation.last_message_at)}
          </time>
        </div>
        {preview(conversation) && (
          <p className="mt-0.5 truncate text-xs text-[#0A2540]/65">{preview(conversation)}</p>
        )}
      </button>
      <button
        type="button"
        aria-label={`${chatText("chat.history.rename")}: ${name}`}
        onClick={() => {
          setTitle(conversation.title || "");
          setIsEditing(true);
        }}
        className="rounded-full p-1.5 text-[#0A4730]/60 hover:bg-[#0A4730]/5"
      >
        <Pencil size={15} />
      </button>
      <button
        type="button"
        aria-label={`${chatText("chat.history.archive")}: ${name}`}
        onClick={() => onArchive(conversation.id)}
        className="rounded-full p-1.5 text-[#0A4730]/60 hover:bg-[#0A4730]/5"
      >
        <Archive size={15} />
      </button>
    </li>
  );
}

export function ChatHistoryPanel({
  conversations = [],
  activeId = null,
  isLoading = false,
  error = null,
  onOpen,
  onNew,
  onRename,
  onArchive,
  onClose,
}) {
  return (
    <section
      aria-label={chatText("chat.history.title")}
      className="flex h-full min-h-0 flex-col bg-[#F7F5F0]"
    >
      <div className="flex items-center justify-between gap-2 border-b border-black/5 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label={chatText("chat.history.back")}
          className="rounded-full p-1.5 text-[#0A4730] hover:bg-[#0A4730]/5"
        >
          <ArrowLeft size={18} />
        </button>
        <h2 className="flex-1 text-sm font-semibold text-[#042416]">{chatText("chat.history.title")}</h2>
        <button
          type="button"
          onClick={onNew}
          className="flex items-center gap-1 rounded-full bg-[#0A4730] px-3 py-1.5 text-xs font-medium text-white"
        >
          <Plus size={14} />
          {chatText("chat.history.new")}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {error && <p className="px-1 text-sm text-[#9B1C1C]">{chatText("chat.history.load_failed")}</p>}

        {!error && !isLoading && conversations.length === 0 && (
          <p className="px-1 text-sm text-[#0A2540]/60">{chatText("chat.history.empty")}</p>
        )}

        <ul className="flex flex-col gap-2">
          {conversations.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              isActive={conversation.id === activeId}
              onOpen={onOpen}
              onRename={onRename}
              onArchive={onArchive}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}
